import {type MediaCardType} from '@wandb/weave/common/types/media';
import {type RefObject, useMemo} from 'react';

import {type ClassLabelMap} from '../../../util/media';
import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode/SingleRunModeContext';
import {useDetectPanelVariant} from '../../Panel/hooks/hooks';
import {type Layout, type MediaConfigWithDefaults} from '../Component';
import {
  adaptGridDataToLayout,
  buildControlData,
  buildRowData,
  buildSlotRegistry,
  dispatchTransform,
  type TransformInput,
} from '../layout';
import {type StepMeta} from '../types';
import {resolveCurrentStep} from '../utils/stepUtils';
import {
  type PanelBuildMetrics,
  recordLayoutGenBuildTiming,
} from '../utils/tracking';

export type LayoutGenerationInput = {
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  stepMeta: StepMeta;
  allMasks: ClassLabelMap;
  configWithDefaults: MediaConfigWithDefaults;
  mediaType: MediaCardType | null;
};

/**
 * Generates the layout structure (tiles, axis data, slots, gridData) from run data and config.
 *
 * On failure, returns `{tiles: []}` without `slots`/`gridData`; the media panel treats that as an error.
 *
 * @param input - Run data, step metadata, and configuration
 * @returns Layout including slot registry and grid data for {@link MediaPanelContent}
 */
export function useLayoutGeneration(
  input: LayoutGenerationInput,
  panelBuildMetricsRef?: RefObject<PanelBuildMetrics>
): Layout {
  const {runsWithMedia, stepMeta, allMasks, configWithDefaults, mediaType} =
    input;

  // Map runs to the format expected by layout transforms
  const mappedRuns = useMemo(() => {
    return runsWithMedia.map(run => ({
      name: run.name,
      displayName: run.displayName,
      entityName: run.entityName,
      projectName: run.projectName,
      history: run.history,
      notes: run.notes,
      color: run.color,
      _wandb: run._wandb,
    }));
  }, [runsWithMedia]);

  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const {isSingleRun: isSingleRunView} = useSingleRunModeContext();

  // Generate layout structure
  const layout = useMemo(() => {
    // oxlint-disable-next-line react/purity
    const startPerformanceMs = performance.now();
    const recordLayoutGenTiming = (layoutReady: boolean) => {
      if (!panelBuildMetricsRef) {
        return;
      }
      recordLayoutGenBuildTiming(
        panelBuildMetricsRef,
        {
          startPerformanceMs,
          // oxlint-disable-next-line react/purity
          durationMs: performance.now() - startPerformanceMs,
        },
        layoutReady
      );
    };

    // Early return if we don't have the required data
    if (
      runsWithMedia.length === 0 ||
      stepMeta.runs.length === 0 ||
      !mediaType
    ) {
      recordLayoutGenTiming(false);
      return {tiles: []};
    }

    try {
      // Prepare input for transform
      const currentStep = resolveCurrentStep(
        configWithDefaults.stepIndex,
        stepMeta,
        configWithDefaults.mediaSliderKey
      );
      const transformInput: TransformInput = {
        runs: mappedRuns,
        stepMeta,
        currentStep,
        currentIndex: configWithDefaults.mediaIndex ?? 0,
        config: configWithDefaults,
        allMasks,
        allBoxes: {}, // TODO: Add box data when available
        mediaType,
        isSingleRunView,
      };

      // Dispatch to appropriate transform
      const gridData = dispatchTransform(transformInput);

      // Adapt to existing Layout structure
      const newLayout = adaptGridDataToLayout(gridData);

      // NEW: Generate slot registry from layout strategy
      const slots = buildSlotRegistry(
        configWithDefaults,
        stepMeta,
        isFullScreenPanelPage === true,
        transformInput.isSingleRunView
      );

      // Three complementary data structures derived from the same transform input:
      //
      // gridData      — the rendered tile grid (rows × columns of cells). Each cell
      //                 holds the actual run/step/index used to fetch media. Answers
      //                 "what is displayed in this tile?" Cells can be empty when a
      //                 run has no data at a given step/index.
      //
      // controlData   — precomputed per-column config for column-header controls
      //                 (step slider value, run selector, media key, etc.). Derived
      //                 from the panel config and x-axis ticks — available before
      //                 cells render and independent of cell emptiness.
      //
      // rowData       — precomputed per-row config for row-header labels (which
      //                 step/run/index a row represents). Derived from y-axis ticks
      //                 and, for compare fan-out, the media-key grouping calculation.
      //                 Needed because empty cells can't supply the row label.
      const controlData = buildControlData(transformInput, gridData);
      const rowData = buildRowData(transformInput, gridData);

      const result = {
        ...newLayout,
        slots,
        gridData,
        controlData,
        rowData,
      };
      recordLayoutGenTiming(result.tiles.length > 0);
      return result;
    } catch (error) {
      console.error('Media panel layout generation failed:', error);
      recordLayoutGenTiming(false);
      return {tiles: []};
    }
  }, [
    mappedRuns,
    stepMeta,
    configWithDefaults,
    allMasks,
    runsWithMedia.length,
    isFullScreenPanelPage,
    isSingleRunView,
    mediaType,
    panelBuildMetricsRef,
  ]);

  return layout;
}
